import styled from 'styled-components';
import _template from 'lodash/template';
import _upperFirst from 'lodash/upperFirst';
import { formatInTimeZone } from 'date-fns-tz';
import { parseISO, format } from 'date-fns';
import { colors, fontSizes } from 'ui/theme';
import { wasRecurring, isReady, isDraft } from 'utils/campaigns';

import { Tooltip } from 'ui/tooltip';

import { CampaignType } from 'modules/domains/campaign';
import { TemplateExecutor } from 'lodash';
import { RecurrenceScheduleType } from 'modules/campaigns/lib/types';
import { NoOpComponent } from 'utils/reactHelpers';
import { getCampaignRecurrenceScheduleType } from 'modules/campaigns/lib/utils';
import { GraphQLCampaign } from '../domainTypes';
import { TableCell } from '../CampaignIndexTable.styles';

export const CAMPAIGN_INDEX_LAUNCH_CELL_TEST_ID = 'campaign-index-launch-cell';

export const CAMPAIGN_INDEX_RECURRENCE_FREQUENCY_TEXT_MAPPER = {
  [RecurrenceScheduleType.DoNotRepeat]: '',
  [RecurrenceScheduleType.Daily]: 'daily',
  [RecurrenceScheduleType.Weekly]: 'weekly',
  [RecurrenceScheduleType.BiWeekly]: 'every two weeks',
  [RecurrenceScheduleType.ThirtyDays]: 'every 30 days',
  [RecurrenceScheduleType.FirstOfTheMonth]: 'every first of the month',
  [RecurrenceScheduleType.FifteenTheOfTheMonth]: 'every 15th of the month',
  [RecurrenceScheduleType.LastOfTheMonth]: 'every last of the month',
  [RecurrenceScheduleType.Custom]: _template(
    'every <%= recurrenceFrequency %> day<%= pluralLetter %>',
  ),
};

const DATE_TIME_FORMAT = 'M/dd/yyyy, h:mm a';
/**
 * Converts an ISO-8601 timestamp string into a string with the following format:
 * "M/DD/YYYY, h:mm A"
 */
export const displayDateTimeWithComma = (timestamp: string | null | undefined) => {
  if (!timestamp) return '';

  return format(parseISO(timestamp), DATE_TIME_FORMAT);
};

/**
 * Returns recurrence frequency for **`RecurrenceScheduleType.Custom`** recurrence types.
 */
export const getDayRecurrenceFrequency = (
  schedulerMsgParams: GraphQLCampaign['schedulerMsgParams'],
) => {
  const period = schedulerMsgParams?.recurSchedule?.period;
  if (period) {
    /** Extracts and parses number from `P${number}D` */
    return parseInt(`${period?.replace(/([PD])/g, '')}`, 10);
  }

  /** We always prioritize  `recurSchedule` values over `recurDays`,
   * but if `recurSchedule` doesn't exist we use `recurDays` */
  return schedulerMsgParams?.recurDays;
};

/**
 * Returns recurrence frequency text for a recurrence type
 */
export const getRecurrenceFrequencyText = (
  type: RecurrenceScheduleType,
  campaign: GraphQLCampaign,
) => {
  const recurrenceFrequency = getDayRecurrenceFrequency(campaign?.schedulerMsgParams);
  const recurrenceFrequencyText = CAMPAIGN_INDEX_RECURRENCE_FREQUENCY_TEXT_MAPPER?.[type];

  if (type === RecurrenceScheduleType.Custom) {
    return (recurrenceFrequencyText as TemplateExecutor)({
      recurrenceFrequency,
      pluralLetter: `${recurrenceFrequency === 1 ? '' : 's'}`,
    });
  }

  return recurrenceFrequencyText as string;
};

const LaunchDateCaption = styled.div`
  color: ${colors.textSecondary};
  font-size: ${fontSizes.s12};
`;

const LaunchDatePlaceholder = styled(TableCell)`
  color: ${colors.textDisabled};
`;

type CampaignLaunchTableCellProps = {
  /** The campaign object for which to display the launch date/details */
  campaign: GraphQLCampaign;
};

const NotLaunchedBlastCampaignTableCell = () => (
  <LaunchDatePlaceholder>Not launched</LaunchDatePlaceholder>
);

const RecurringBlastCampaignTableCell = ({ campaign }: CampaignLaunchTableCellProps) => {
  const hasEndDate = !!campaign?.schedulerMsgParams?.endDate;
  const type = getCampaignRecurrenceScheduleType(campaign);
  const recurrenceFrequencyText = _upperFirst(getRecurrenceFrequencyText(type, campaign));

  return (
    <TableCell data-test={CAMPAIGN_INDEX_LAUNCH_CELL_TEST_ID}>
      <div>
        <div>{displayDateTimeWithComma(campaign?.startAt)}</div>
        <LaunchDateCaption data-test={`${CAMPAIGN_INDEX_LAUNCH_CELL_TEST_ID}-recurrence-frequency`}>
          {recurrenceFrequencyText}
          {hasEndDate && (
            <>
              , until&nbsp;
              <Tooltip
                isUnderlined
                data-test="campaign-schedule-end-date-tooltip-content"
                tooltipContent={
                  campaign?.schedulerMsgParams?.endDate?.timestamp && (
                    <span>
                      {formatInTimeZone(
                        campaign.schedulerMsgParams.endDate.timestamp,
                        campaign.schedulerMsgParams.endDate.timezone,
                        DATE_TIME_FORMAT,
                      )}
                    </span>
                  )
                }
              >
                end
              </Tooltip>
            </>
          )}
        </LaunchDateCaption>
      </div>
    </TableCell>
  );
};

const DefaultBlastCampaignTableCell = ({ campaign }: CampaignLaunchTableCellProps) => (
  <TableCell data-test={CAMPAIGN_INDEX_LAUNCH_CELL_TEST_ID}>
    <div>
      <div>{displayDateTimeWithComma(campaign.startAt)}</div>
      {campaign.endedAt && (
        <LaunchDateCaption>Ended {displayDateTimeWithComma(campaign.endedAt)}</LaunchDateCaption>
      )}
    </div>
  </TableCell>
);

const BlastCampaignLaunchTableCell = ({ campaign }: CampaignLaunchTableCellProps) => {
  if (wasRecurring(campaign)) {
    return <RecurringBlastCampaignTableCell campaign={campaign} />;
  }

  if (isReady(campaign) || isDraft(campaign) || !campaign.startAt) {
    return <NotLaunchedBlastCampaignTableCell />;
  }

  return <DefaultBlastCampaignTableCell campaign={campaign} />;
};

/**
 * If a campaign is triggered by API, it should display the
 * workflow trigger captions regardless of the campaign state
 */
const ApiTriggeredCampaignLaunchTableCell = () => (
  <LaunchDatePlaceholder>API-Triggered</LaunchDatePlaceholder>
);

/**
 * If a campaign is triggered by workflow, it should display the
 * workflow trigger captions regardless of the campaign state
 */
const JourneyTriggeredCampaignLaunchTableCell = () => (
  <LaunchDatePlaceholder>Journey triggered</LaunchDatePlaceholder>
);

const CAMPAIGN_TYPE_LAUNCH_TABLE_CELL_MAPPER = {
  [CampaignType.Blast]: BlastCampaignLaunchTableCell,
  [CampaignType.Eligibility]: BlastCampaignLaunchTableCell,
  [CampaignType.ApiTriggered]: ApiTriggeredCampaignLaunchTableCell,
  [CampaignType.WorkflowTriggered]: JourneyTriggeredCampaignLaunchTableCell,
  /** NOTE: These doesn't represent a campaign in our database, they're just used for filtering campaigns */
  [CampaignType.WithAI]: NoOpComponent,
  [CampaignType.WithExperiment]: NoOpComponent,
};

export const CampaignLaunchTableCell = ({ campaign }: CampaignLaunchTableCellProps) => {
  const { campaignTypeDefinition } = campaign;

  const CampaignTypeLaunchTableCell =
    CAMPAIGN_TYPE_LAUNCH_TABLE_CELL_MAPPER?.[campaignTypeDefinition] ?? NoOpComponent;
  return <CampaignTypeLaunchTableCell campaign={campaign} />;
};
